<script lang="ts">
  import type { VideoData, Conversation, ChatMessage, User } from '../lib/types'
  import { hasPermission } from '../lib/types'
  import { createConversation, sendChatMessage, getConversations, getConversation } from '../lib/chatApi'

  interface Props {
    videoId: string | null
    videoData: VideoData | null
    currentTime: number
    currentUser: User | null
    isLoadingSnippets: boolean
    isFetchingDescription: boolean
    pendingSnippetIndex: number | null
    style?: string
    onSeekToTime: (seconds: number) => void
    onSeekToSnippet: (seconds: number, snippetIndex: number) => void
    onFetchDescription: () => void
    onTranscriptScroll: () => void
    onPlay: () => void
  }

  let {
    videoId,
    videoData,
    currentTime,
    currentUser,
    isLoadingSnippets,
    isFetchingDescription,
    pendingSnippetIndex,
    style = '',
    onSeekToTime,
    onSeekToSnippet,
    onFetchDescription,
    onTranscriptScroll,
    onPlay,
  }: Props = $props()

  let transcriptMode = $state<'transcript' | 'description' | 'chat'>('description')
  let transcriptSearchQuery = $state('')
  let debouncedSearchQuery = $state('')
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let conversation = $state<Conversation | null>(null)
  let chatInput = $state('')
  let isSendingMessage = $state(false)
  let chatError = $state<string | null>(null)
  let chatMessagesContainer: HTMLDivElement
  let transcriptList: HTMLDivElement

  $effect(() => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      debouncedSearchQuery = transcriptSearchQuery
    }, 200)
    return () => {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    }
  })

  let indexedSnippets = $derived(videoData?.structured_data?.snippets || [])

  let filteredSnippets = $derived.by(() => {
    if (!debouncedSearchQuery.trim()) return indexedSnippets
    const query = debouncedSearchQuery.toLowerCase()
    return indexedSnippets.filter(s => s.text.toLowerCase().includes(query))
  })

  function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  function highlightSearchMatch(text: string): string {
    if (!debouncedSearchQuery.trim()) return text
    const query = debouncedSearchQuery.trim()
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')
    return text.replace(regex, '<mark class="bg-yellow-500/40 text-inherit rounded px-0.5">$1</mark>')
  }

  function isActiveSnippet(snippet: { start: number; duration: number }): boolean {
    return currentTime >= snippet.start && currentTime < snippet.start + snippet.duration
  }

  function linkifyDescription(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    const urlPattern = /(https?:\/\/[^\s]+)/g
    const withLinks = escaped.replace(urlPattern, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-accent-primary hover:underline">$1</a>')

    return withLinks.replace(/\n/g, '<br>')
  }

  function parseTimestampToSeconds(timestamp: string): number {
    const parts = timestamp.split(':').map(Number)
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2]
    }
    return parts[0] * 60 + parts[1]
  }

  function linkifyChatContent(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    const timestampPattern = /\b(\d{1,2}:\d{2}(?::\d{2})?)\b/g
    const withTimestamps = escaped.replace(timestampPattern, (match) => {
      const seconds = parseTimestampToSeconds(match)
      return `<button class="inline px-1.5 py-0.5 text-xs bg-surface-3 border border-border-default rounded text-accent-primary cursor-pointer hover:bg-surface-4 hover:text-accent-hover" data-seconds="${seconds}">${match}</button>`
    })

    return withTimestamps.replace(/\n/g, '<br>')
  }

  function handleChatContentClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (target.dataset.seconds) {
      const seconds = parseFloat(target.dataset.seconds)
      onSeekToTime(seconds)
    }
  }

  function switchToDescription() {
    transcriptMode = 'description'
    if (!videoData?.description && !isFetchingDescription) {
      onFetchDescription()
    }
  }

  async function initializeChat() {
    if (!videoId) return
    try {
      chatError = null
      const conversations = await getConversations(videoId)
      if (conversations.length > 0) {
        conversation = await getConversation(videoId, conversations[0].id)
      } else {
        conversation = await createConversation(videoId)
      }
    } catch (e) {
      chatError = 'Failed to start conversation'
      console.error(e)
    }
  }

  async function handleSendMessage() {
    if (!videoId || !conversation || !chatInput.trim() || isSendingMessage) return

    const message = chatInput.trim()
    chatInput = ''
    isSendingMessage = true
    chatError = null

    conversation = {
      ...conversation,
      messages: [
        ...conversation.messages,
        { id: -1, role: 'user', content: message, created_at: new Date().toISOString() } as ChatMessage,
      ],
    }

    try {
      const result = await sendChatMessage(videoId, conversation.id, message, currentTime)
      conversation = result.conversation

      setTimeout(() => {
        if (chatMessagesContainer) {
          chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight
        }
      }, 50)
    } catch (e) {
      chatError = 'Failed to send message'
      console.error(e)
      conversation = {
        ...conversation,
        messages: conversation.messages.filter(m => m.id !== -1),
      }
    } finally {
      isSendingMessage = false
    }
  }

  function handleChatKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  async function startNewConversation() {
    if (!videoId) return
    try {
      chatError = null
      conversation = await createConversation(videoId)
    } catch (e) {
      chatError = 'Failed to start new conversation'
      console.error(e)
    }
  }

  function getSuggestedQuestions(): string[] {
    const analysisQuestions = videoData?.structured_data?.suggested_questions as string[] | undefined
    if (analysisQuestions?.length) {
      return analysisQuestions.slice(0, 5)
    }

    const questions: string[] = []
    const topics = videoData?.topics || []
    const persons = videoData?.persons || []

    if (topics.length > 0) {
      questions.push(`What does "${topics[0].name}" mean in this video?`)
    }
    if (persons.length > 0) {
      questions.push(`Who is ${persons[0].name} and what do they discuss?`)
    }
    if (questions.length < 2) {
      questions.push('What are the main topics discussed?')
    }

    return questions.slice(0, 3)
  }

  function selectSuggestedQuestion(question: string) {
    chatInput = question
  }

  function formatToolName(name: string): string {
    const mainPart = name.includes('--') ? name.split('--').pop()! : name
    return mainPart
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
  }

  function formatToolArgs(args: Record<string, unknown>): string {
    return Object.entries(args)
      .map(([key, value]) => {
        const formattedKey = key.replace(/_/g, ' ')
        const formattedValue = typeof value === 'string' ? value : JSON.stringify(value)
        return `${formattedKey}: ${formattedValue}`
      })
      .join(', ')
  }

  export function setMode(mode: 'transcript' | 'description' | 'chat') {
    transcriptMode = mode
  }

  export function getTranscriptList(): HTMLDivElement | undefined {
    return transcriptList
  }
</script>

<section data-testid="transcript-panel" class="flex flex-col bg-surface-1 border-l border-border-default overflow-hidden" {style}>
  <div class="flex items-center justify-between px-3 h-10 border-b border-border-default shrink-0">
    <div class="flex rounded overflow-hidden">
      <button
        class="h-6 px-2.5 text-xs border-none cursor-pointer transition-colors {transcriptMode === 'description' ? 'bg-surface-4 text-text-primary' : 'bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-secondary'}"
        aria-pressed={transcriptMode === 'description'}
        onclick={switchToDescription}
      >
        Description
      </button>
      <button
        class="h-6 px-2.5 text-xs border-none cursor-pointer transition-colors {transcriptMode === 'transcript' ? 'bg-surface-4 text-text-primary' : 'bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-secondary'}"
        aria-pressed={transcriptMode === 'transcript'}
        onclick={() => { transcriptMode = 'transcript'; onPlay() }}
      >
        Transcript
      </button>
      {#if hasPermission(currentUser, 'can_chat')}
        <button
          class="h-6 px-2.5 text-xs border-none cursor-pointer transition-colors {transcriptMode === 'chat' ? 'bg-surface-4 text-text-primary' : 'bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-secondary'}"
          aria-pressed={transcriptMode === 'chat'}
          onclick={() => { transcriptMode = 'chat'; if (!conversation) initializeChat(); }}
        >
          Chat
        </button>
      {/if}
    </div>
    {#if transcriptMode === 'transcript'}
      <div class="relative flex items-center">
        <input
          type="text"
          placeholder="Search..."
          bind:value={transcriptSearchQuery}
          class="w-32 h-6 px-2 pr-6 text-xs bg-surface-2 border border-border-default rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-strong"
        />
        {#if transcriptSearchQuery}
          <button
            class="absolute right-1.5 w-4 h-4 flex items-center justify-center bg-transparent border-none text-text-muted text-sm cursor-pointer hover:text-text-primary"
            onclick={() => transcriptSearchQuery = ''}
          >
            ×
          </button>
        {/if}
      </div>
    {/if}
  </div>

  {#if transcriptMode === 'description'}
    {#if isFetchingDescription}
      <div class="flex items-center justify-center gap-2 p-4 text-sm text-text-muted">
        <span class="w-4 h-4 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></span>
        Loading description...
      </div>
    {:else if videoData?.description}
      <div class="flex-1 overflow-y-auto p-4 text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
        {@html linkifyDescription(videoData.description)}
      </div>
    {:else}
      <div class="flex items-center justify-center p-4 text-sm text-text-muted">No description available</div>
    {/if}
  {:else if transcriptMode === 'chat'}
    <div class="flex flex-col flex-1 overflow-hidden">
      {#if conversation?.messages?.length}
        <div class="flex items-center justify-between px-3 py-2 border-b border-border-default">
          <button
            class="h-6 px-2 text-xs bg-surface-2 border border-border-default rounded text-text-secondary cursor-pointer hover:bg-surface-3 hover:text-text-primary"
            onclick={startNewConversation}
            title="Start new conversation"
          >
            + New chat
          </button>
        </div>
      {/if}
      <div
        class="flex-1 overflow-y-auto p-3 space-y-3"
        bind:this={chatMessagesContainer}
        onclick={handleChatContentClick}
      >
        {#if conversation?.messages?.length}
          {#each conversation.messages as message}
            <div class="rounded-lg p-3 {message.role === 'user' ? 'bg-surface-3 ml-4' : 'bg-surface-2 mr-4'}">
              <div class="text-[10px] font-medium mb-1.5 {message.role === 'user' ? 'text-accent-primary' : 'text-accent-success'}">
                {message.role === 'user' ? 'You' : 'Assistant'}
              </div>
              {#if message.role === 'assistant' && (message.metadata?.thinking_seconds || message.tool_calls?.length)}
                <details class="mb-2 text-xs">
                  <summary class="cursor-pointer text-text-muted hover:text-text-secondary py-1">
                    {#if message.metadata?.thinking_seconds}
                      Thought for {message.metadata.thinking_seconds}s{#if message.tool_calls?.length}, using <span class="text-accent-primary">{message.tool_calls.length} tool{message.tool_calls.length > 1 ? 's' : ''}</span>{/if}
                    {:else if message.tool_calls?.length}
                      Used <span class="text-accent-primary">{message.tool_calls.length} tool{message.tool_calls.length > 1 ? 's' : ''}</span>
                    {:else}
                      Processed
                    {/if}
                  </summary>
                  {#if message.tool_calls?.length}
                    <div class="mt-2 pl-3 border-l-2 border-border-default space-y-1">
                      {#each message.tool_calls as tool}
                        <div class="text-text-muted">
                          <span class="text-text-secondary">{formatToolName(tool.name)}</span>
                          <span class="text-text-muted ml-1">{formatToolArgs(tool.arguments)}</span>
                        </div>
                      {/each}
                    </div>
                  {/if}
                </details>
              {/if}
              <div class="text-sm text-text-primary leading-relaxed">{@html linkifyChatContent(message.content)}</div>
            </div>
          {/each}
        {:else}
          <div class="flex flex-col items-center justify-center h-full text-center p-4">
            <p class="text-base font-medium text-text-primary mb-2">Ask questions about the video</p>
            <p class="text-xs text-text-muted mb-4">The assistant can search the transcript and reference specific timestamps.</p>
            {#if videoData?.structured_data}
              <div class="w-full space-y-2">
                <p class="text-xs text-text-muted">Try asking:</p>
                {#each getSuggestedQuestions() as question}
                  <button
                    class="w-full text-left px-3 py-2 text-xs bg-surface-2 border border-border-default rounded text-text-secondary cursor-pointer hover:bg-surface-3 hover:border-border-strong hover:text-text-primary transition-colors"
                    onclick={() => selectSuggestedQuestion(question)}
                  >
                    {question}
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
        {#if isSendingMessage}
          <details class="rounded-lg p-3 bg-surface-2 mr-4" open>
            <summary class="flex items-center gap-2 cursor-pointer text-text-muted text-xs">
              <span class="w-3 h-3 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></span>
              <span>Thinking...</span>
            </summary>
            <div class="mt-2 text-xs text-text-muted pl-5">
              Analyzing your question and searching relevant transcript sections...
            </div>
          </details>
        {/if}
      </div>
      {#if chatError}
        <div class="px-3 py-2 text-xs text-red-400 bg-red-500/10">{chatError}</div>
      {/if}
      <div class="flex gap-2 p-3 border-t border-border-default">
        <textarea
          class="flex-1 min-h-[60px] p-2 text-sm bg-surface-2 border border-border-default rounded text-text-primary resize-none placeholder:text-text-muted focus:outline-none focus:border-border-strong disabled:opacity-50"
          bind:value={chatInput}
          placeholder="Ask about the video..."
          onkeydown={handleChatKeydown}
          disabled={isSendingMessage}
          rows="2"
        ></textarea>
        <button
          class="self-end h-9 px-4 text-sm bg-accent-primary border-none rounded text-white cursor-pointer hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
          onclick={handleSendMessage}
          disabled={!chatInput.trim() || isSendingMessage}
        >
          Send
        </button>
      </div>
    </div>
  {:else if videoData?.transcript_error}
    <div class="flex items-center gap-2 px-4 py-3 m-3 bg-red-500/10 border border-red-500/30 rounded">
      <span class="text-red-400 text-base">⚠</span>
      <span class="text-sm text-red-300">{videoData.transcript_error_message}</span>
    </div>
  {:else if videoData?.structured_data?.snippets}
    <div class="flex-1 overflow-y-auto" bind:this={transcriptList} onscroll={onTranscriptScroll}>
      {#each filteredSnippets as snippet}
        <div
          class="flex gap-2 px-3 py-1.5 border-b border-border-subtle transition-colors {isActiveSnippet(snippet) ? 'bg-surface-3' : ''} {pendingSnippetIndex === snippet.originalIndex ? 'bg-accent-primary/20' : ''}"
          data-index={snippet.originalIndex}
        >
          <button
            class="shrink-0 w-12 text-xs text-text-muted bg-transparent border-none cursor-pointer text-left hover:text-accent-primary"
            onclick={() => onSeekToSnippet(snippet.start, snippet.originalIndex)}
          >
            {formatTime(snippet.start)}
          </button>
          {#if debouncedSearchQuery.trim()}
            <span class="text-sm text-text-secondary leading-relaxed {isActiveSnippet(snippet) ? 'text-text-primary' : ''}">{@html highlightSearchMatch(snippet.text)}</span>
          {:else}
            <span class="text-sm text-text-secondary leading-relaxed {isActiveSnippet(snippet) ? 'text-text-primary' : ''}">{snippet.text}</span>
          {/if}
        </div>
      {/each}
      {#if debouncedSearchQuery.trim() && filteredSnippets.length === 0}
        <div class="flex items-center justify-center p-4 text-sm text-text-muted">No matches found</div>
      {/if}
    </div>
  {:else if isLoadingSnippets}
    <div class="flex items-center justify-center gap-2 p-4 text-sm text-text-muted">
      <span class="w-4 h-4 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></span>
      Loading transcript...
    </div>
  {:else if !videoData}
    <div class="flex items-center justify-center p-4 text-sm text-text-muted">Loading...</div>
  {:else}
    <div class="flex items-center justify-center p-4 text-sm text-text-muted">No transcript available</div>
  {/if}
</section>
